import React from 'react';
import { Button } from './ui/button';
import { School, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * Dedicated band for Clubs, Schools & Athletic Directors. Lifted out of the
 * <AudienceSegmentation /> 4-card grid so it doesn't read as an isolated
 * fifth tile and so the program-level offer (workshops + multi-team
 * engagements) gets a clear visual identity of its own.
 *
 * Visual contract:
 *  - Tinted band (subtle midnight-green wash) so it threads continuously
 *    out of "Who We Serve" without feeling like a separate orphan section
 *  - Single planning CTA, no audience list duplication
 *  - Bullet list rendered with `gap-3` for proper space after the bullet
 *    glyph (matches the convention in AudienceSegmentation post-fix).
 */

const POINTS = [
  'Whole-program mental performance gaps across teams',
  'Need a credentialed sports psychology resource — not a guest speaker',
  'Want measurable buy-in from coaches, athletes, and parents',
  'Looking for repeatable workshops, not one-off assemblies',
];

const fadeIn = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

export const ClubsBand = ({ calendlyLink }) => {
  return (
    <section
      id="clubs"
      className="py-16 sm:py-20 bg-gradient-to-b from-[#F0F4F4] to-white"
      data-testid="clubs-band-section"
      aria-labelledby="clubs-band-heading"
    >
      <div className="max-w-[1100px] mx-auto px-5 sm:px-6">
        <motion.div
          {...fadeIn}
          className="rounded-2xl border border-[#004C54]/15 bg-white shadow-[0_8px_28px_rgba(0,76,84,0.08)] overflow-hidden"
        >
          <div className="grid lg:grid-cols-[1fr_0.85fr] gap-0">
            {/* Left: pitch */}
            <div className="p-7 sm:p-10">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl bg-[#004C54] flex items-center justify-center">
                  <School className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54] font-bold">
                  For Clubs & Team Directors
                </div>
              </div>
              <h2
                id="clubs-band-heading"
                className="font-heading text-[1.6rem] sm:text-[2rem] font-bold tracking-tight leading-[1.15] text-[#0a0d0e]"
              >
                A repeatable mental performance framework
                <br className="hidden sm:block" />
                across every program in your athletic department.
              </h2>
              <p className="mt-4 text-[15px] sm:text-[16px] text-[#3a3a3a] leading-relaxed max-w-[58ch]">
                Built for schools, clubs, and travel programs that want measurable mental-game
                results across coaches, athletes, and parents — not a one-off guest assembly.
              </p>

              <ul className="mt-6 space-y-2.5">
                {POINTS.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-[#3a3a3a]"
                    data-testid={`clubs-band-bullet-${point.split(' ')[0].toLowerCase()}`}
                  >
                    <CheckCircle2 className="h-[18px] w-[18px] text-[#4FBCC7] shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="flex-1">{point}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7">
                <Button
                  asChild
                  className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-12 px-7 rounded-xl"
                >
                  <a href={calendlyLink} data-testid="clubs-band-cta">
                    Book a Planning Call
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </a>
                </Button>
                <p className="text-[12px] text-[#516570] mt-3">
                  60–90 min workshops · multi-session packages · custom by sport and program
                </p>
              </div>
            </div>

            {/* Right: outcome stripe */}
            <div className="hidden lg:flex flex-col justify-center bg-[#004C54] text-white p-10">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#4FBCC7] font-bold mb-3">
                What you walk away with
              </div>
              <ul className="space-y-3 text-[15px] leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="text-[#4FBCC7] font-bold mt-px" aria-hidden="true">&bull;</span>
                  <span>A shared mental-performance language across coaches, athletes, and parents</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#4FBCC7] font-bold mt-px" aria-hidden="true">&bull;</span>
                  <span>Pre-game and in-season routines your coaches can run without me in the room</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#4FBCC7] font-bold mt-px" aria-hidden="true">&bull;</span>
                  <span>A measurable lift in composure, focus, and confidence in big moments</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClubsBand;
